"use client";

import { AuthErrorEnum } from "@pharaday/auth";
import { Alert, type AlertRootProps, PharadayTextLogoIcon } from "@pharaday/lighthouse";
import { ContainerProvider } from "@pharaday/utils";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { StepVerifyEmail } from "../../components";
import { UnauthenticatedContainer } from "../../core/unauthenticated.container";
import StepInputEmailOTP from "./StepInputEmailOTP";
import StepLogin from "./StepLogin";

export interface InternalLoginFormProps {
  loginCallbackUrl: string;
}

enum FormStepEnum {
  LOGIN = "LOGIN",
  INPUT_EMAIL_OTP = "INPUT_EMAIL_OTP",
  VERIFY_EMAIL = "VERIFY_EMAIL",
}

const InternalLoginForm = ({ loginCallbackUrl }: InternalLoginFormProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [alert, setAlert] = useState<{ type: AlertRootProps["type"]; message: string }>();
  const [step, setStep] = useState<FormStepEnum>(FormStepEnum.LOGIN);
  const [pendingToken, setPendingToken] = useState<string>();
  const [otpEmail, setOtpEmail] = useState<string>();

  const handleCloseAlert = () => setAlert(undefined);

  useEffect(() => {
    const error = searchParams.get("error");
    const isJustRegistered = searchParams.get("registered") === "true";

    switch (error) {
      case AuthErrorEnum.REFRESH_ACCESS_TOKEN_ERROR: {
        setAlert({
          message: "Your session has expired. Please login again",
          type: "primary",
        });
        break;
      }

      case AuthErrorEnum.SSO_ERROR: {
        setAlert({
          message: "An error occurred while logging in with SSO. Please contact your admin for more information.",
          type: "error",
        });
        break;
      }

      default: {
        if (isJustRegistered) {
          setAlert({
            message: "Your account has been created successfully. Please log in with your credentials.",
            type: "success",
          });
        }
        break;
      }
    }
  }, [searchParams]);

  const handleLoginSuccess = () => {
    router.push(loginCallbackUrl);
  };

  const handleRequireEmailVerification = (pendingToken: string) => {
    setPendingToken(pendingToken);
    setStep(FormStepEnum.VERIFY_EMAIL);
  };

  const handleRequireEmailOTP = (email: string) => {
    setOtpEmail(email);
    setStep(FormStepEnum.INPUT_EMAIL_OTP);
  };

  const handleChangeEmail = () => {
    setAlert(undefined);
    setOtpEmail(undefined);
    setStep(FormStepEnum.LOGIN);
  };

  return (
    <ContainerProvider value={UnauthenticatedContainer}>
      <div className="flex flex-col gap-6">
        {alert && (
          <Alert.Root className="mb-4" orientation="horizontal" type={alert.type}>
            <Alert.IconLayout title={alert.message}></Alert.IconLayout>
            <Alert.Close onClick={handleCloseAlert} />
          </Alert.Root>
        )}

        <PharadayTextLogoIcon className="mb-4" />

        {step === FormStepEnum.LOGIN && (
          <StepLogin
            onSuccess={handleLoginSuccess}
            onRequireEmailVerification={handleRequireEmailVerification}
            onRequireEmailOTP={handleRequireEmailOTP}
          />
        )}

        {step === FormStepEnum.INPUT_EMAIL_OTP && otpEmail && (
          <StepInputEmailOTP
            email={otpEmail}
            onContinue={handleLoginSuccess}
            onChangeEmail={handleChangeEmail}
            onAlert={setAlert}
          />
        )}

        {step === FormStepEnum.VERIFY_EMAIL && (
          <>
            <h1 className="text-2xl">Account Activation</h1>
            <StepVerifyEmail onContinue={handleLoginSuccess} pendingToken={pendingToken} onAlert={setAlert} />
          </>
        )}
      </div>
    </ContainerProvider>
  );
};

export default InternalLoginForm;
